import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

// Order kinds that speedup items can be applied to. Mirrors server-side
// enum SpeedupOrderKind.
export const SpeedupOrderKind = {
  Build: 0,
  Training: 1,
  Hospital: 2,
  Temple: 3,
  Research: 4,
} as const;

export interface UseSpeedupRequest {
  itemId: string;
  orderKind: number;       // SpeedupOrderKind value
  buildingType?: number;   // required for Build / Training
}

export function useApplySpeedup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: UseSpeedupRequest) => {
      const res = await api.post("/inventory/use-speedup", req);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["castle"] }),
  });
}

export interface OpenBundleRequest {
  itemId: string;
  count?: number;
}

// Opens a ResourceBundle / HeroCrystal / HeroShard item (server discriminates
// by kind on the catalog entry).
export function useOpenBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: OpenBundleRequest) => {
      const res = await api.post("/inventory/open-bundle", {
        itemId: req.itemId,
        count: req.count ?? 1,
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["castle"] }),
  });
}

export function useApplyShield() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      const res = await api.post("/inventory/use-shield", { itemId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["castle"] }),
  });
}

export function useApplyBuff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      const res = await api.post("/inventory/use-buff", { itemId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["castle"] }),
  });
}

// Mirror of InventoryCatalog. Kept in sync manually — small enough not to
// warrant an extra round-trip on every load.
export type InventoryItemKind =
  | "speedup"
  | "bundle"
  | "shield"
  | "buff"
  | "heroCrystal"
  | "heroShard";

export interface InventoryItemDef {
  id: string;
  title: string;
  description: string;
  kind: InventoryItemKind;
  speedupSeconds?: number;
  // Speedup: if set, this item only applies to that specific OrderKind
  // (0=Build / 1=Training / 2=Hospital / 4=Research; heal-speedup uses 2
  // server-side, which fallbacks to Temple if no Hospital order active).
  appliesToOrderKind?: number;
  shieldDurationSeconds?: number;
  buffKind?: string;
  buffDurationSeconds?: number;
  buffMagnitude?: number;
  heroCrystalTier?: number;   // 1=Dim / 2=Shining / 3=Ancient
  heroShardRarity?: number;   // 0=Common / 1=Rare / 2=Epic / 3=Legendary
}

function speedup(id: string, label: string, seconds: number, orderKind?: number, descSuffix = ""): InventoryItemDef {
  const desc = orderKind === undefined
    ? `Сокращает любой таймер на ${label}.`
    : `Сокращает таймер ${descSuffix} на ${label}.`;
  return {
    id, title: orderKind === undefined ? `Ускоритель ${label}` : `Ускоритель ${descSuffix} ${label}`,
    description: desc,
    kind: "speedup",
    speedupSeconds: seconds,
    appliesToOrderKind: orderKind,
  };
}

function bundle(id: string, title: string, description: string): InventoryItemDef {
  return { id, title, description, kind: "bundle" };
}

export const InventoryItems: InventoryItemDef[] = [
  // Universal speedups (6 tiers)
  speedup("spd-1m",  "1 минута", 60),
  speedup("spd-5m",  "5 минут",  300),
  speedup("spd-30m", "30 минут", 1800),
  speedup("spd-1h",  "1 час",    3600),
  speedup("spd-8h",  "8 часов",  28800),
  speedup("spd-24h", "24 часа",  86400),

  // Build speedups
  speedup("spd-build-5m",  "5 минут",  300,   0, "стройки"),
  speedup("spd-build-30m", "30 минут", 1800,  0, "стройки"),
  speedup("spd-build-1h",  "1 час",    3600,  0, "стройки"),
  speedup("spd-build-3h",  "3 часа",   10800, 0, "стройки"),

  // Training speedups
  speedup("spd-train-5m",  "5 минут",  300,   1, "тренировки"),
  speedup("spd-train-30m", "30 минут", 1800,  1, "тренировки"),
  speedup("spd-train-1h",  "1 час",    3600,  1, "тренировки"),
  speedup("spd-train-3h",  "3 часа",   10800, 1, "тренировки"),

  // Research speedups
  speedup("spd-research-5m",  "5 минут",  300,   4, "исследования"),
  speedup("spd-research-30m", "30 минут", 1800,  4, "исследования"),
  speedup("spd-research-1h",  "1 час",    3600,  4, "исследования"),
  speedup("spd-research-3h",  "3 часа",   10800, 4, "исследования"),

  // Heal speedups (Hospital — fallback Temple via server logic).
  speedup("spd-heal-5m",  "5 минут",  300,   2, "лечения"),
  speedup("spd-heal-30m", "30 минут", 1800,  2, "лечения"),
  speedup("spd-heal-1h",  "1 час",    3600,  2, "лечения"),
  speedup("spd-heal-3h",  "3 часа",   10800, 2, "лечения"),

  // Resource bundles
  bundle("bundle-gold-s",  "Малый сундук золота",   "+5 000 золота при открытии."),
  bundle("bundle-gold-m",  "Средний сундук золота", "+25 000 золота при открытии."),
  bundle("bundle-gold-l",  "Большой сундук золота", "+100 000 золота при открытии."),
  bundle("bundle-wood-s",  "Малый сундук дерева",   "+5 000 дерева при открытии."),
  bundle("bundle-wood-m",  "Средний сундук дерева", "+25 000 дерева при открытии."),
  bundle("bundle-wood-l",  "Большой сундук дерева", "+100 000 дерева при открытии."),
  bundle("bundle-stone-s", "Малый сундук камня",    "+5 000 камня при открытии."),
  bundle("bundle-stone-m", "Средний сундук камня",  "+25 000 камня при открытии."),
  bundle("bundle-stone-l", "Большой сундук камня",  "+100 000 камня при открытии."),
  bundle("bundle-mana-s",  "Малый сундук маны",     "+1 000 маны при открытии."),
  bundle("bundle-mana-m",  "Средний сундук маны",   "+5 000 маны при открытии."),
  bundle("bundle-mana-l",  "Большой сундук маны",   "+20 000 маны при открытии."),
  bundle("bundle-mixed-s", "Малый сборный сундук",  "+2 500 каждого ресурса + 500 маны."),
  bundle("bundle-mixed-m", "Средний сборный сундук", "+12 500 каждого ресурса + 2 500 маны."),
  bundle("bundle-mixed-l", "Большой сборный сундук", "+50 000 каждого ресурса + 10 000 маны."),

  // Shields
  {
    id: "shield-8h", title: "Щит 8 часов",
    description: "Защищает замок от PvP-атак на 8 часов.",
    kind: "shield", shieldDurationSeconds: 8 * 3600,
  },
  {
    id: "shield-24h", title: "Щит 24 часа",
    description: "Защищает замок от PvP-атак на 24 часа.",
    kind: "shield", shieldDurationSeconds: 24 * 3600,
  },
  {
    id: "shield-3d", title: "Щит 3 дня",
    description: "Защищает замок от PvP-атак на 72 часа.",
    kind: "shield", shieldDurationSeconds: 72 * 3600,
  },

  // Buffs
  {
    id: "buff-attack-24h", title: "Бафф «Боевая ярость»",
    description: "+20% к атаке войск на 24 часа.",
    kind: "buff", buffKind: "attack",
    buffDurationSeconds: 24 * 3600, buffMagnitude: 0.20,
  },
  {
    id: "buff-defense-24h", title: "Бафф «Каменная кожа»",
    description: "+20% к защите войск на 24 часа.",
    kind: "buff", buffKind: "defense",
    buffDurationSeconds: 24 * 3600, buffMagnitude: 0.20,
  },
  {
    id: "buff-production-8h", title: "Бафф «Жатва»",
    description: "+50% к производству ресурсов на 8 часов.",
    kind: "buff", buffKind: "production",
    buffDurationSeconds: 8 * 3600, buffMagnitude: 0.50,
  },
  {
    id: "buff-train-8h", title: "Бафф «Дисциплина»",
    description: "+30% к скорости тренировки на 8 часов.",
    kind: "buff", buffKind: "training",
    buffDurationSeconds: 8 * 3600, buffMagnitude: 0.30,
  },

  // Hero crystals
  {
    id: "crystal-dim", title: "Тусклый кристалл",
    description: "Открытие даёт +1 Тусклый кристалл героя.",
    kind: "heroCrystal", heroCrystalTier: 1,
  },
  {
    id: "crystal-shining", title: "Сияющий кристалл",
    description: "Открытие даёт +1 Сияющий кристалл героя.",
    kind: "heroCrystal", heroCrystalTier: 2,
  },
  {
    id: "crystal-ancient", title: "Древний кристалл",
    description: "Открытие даёт +1 Древний кристалл героя.",
    kind: "heroCrystal", heroCrystalTier: 3,
  },

  // Universal hero shards (as items)
  {
    id: "shard-common", title: "Универсальные осколки (Common)",
    description: "Открытие даёт +10 универсальных осколков Common.",
    kind: "heroShard", heroShardRarity: 0,
  },
  {
    id: "shard-rare", title: "Универсальные осколки (Rare)",
    description: "Открытие даёт +10 универсальных осколков Rare.",
    kind: "heroShard", heroShardRarity: 1,
  },
  {
    id: "shard-epic", title: "Универсальные осколки (Epic)",
    description: "Открытие даёт +10 универсальных осколков Epic.",
    kind: "heroShard", heroShardRarity: 2,
  },
  {
    id: "shard-legendary", title: "Универсальные осколки (Legendary)",
    description: "Открытие даёт +10 универсальных осколков Legendary.",
    kind: "heroShard", heroShardRarity: 3,
  },
];

export const InventoryItemMap: Record<string, InventoryItemDef> = Object.fromEntries(
  InventoryItems.map((it) => [it.id, it]),
);

// UI grouping. Filter chips at the top of the inventory grid use these.
export type InventoryGroup = "all" | "speedup" | "bundle" | "shield" | "buff" | "hero";

export function groupOf(item: InventoryItemDef): InventoryGroup {
  if (item.kind === "speedup") return "speedup";
  if (item.kind === "bundle") return "bundle";
  if (item.kind === "shield") return "shield";
  if (item.kind === "buff") return "buff";
  return "hero"; // heroCrystal | heroShard
}
